import {useCallback} from 'react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import {
    type AstView,
    type EnrichedAstNode,
    groupNodeBuilder,
    findNodeById,
    findRootsOfSelection,
    findLowestSharedParent,
    getAllChildren,
} from 'rendition-core';
import {
    checkIfCanMoveChildrenUp,
    retrieveGroupingContext,
    moveChildrenUp,
    removeNodeFromView,
    removeNodeAndChildrenFromView,
} from 'rendition-core/Ast/GroupingUtilities';
import {findBetterParent} from 'rendition-core/layout/FindBetterParent/FindBetterParent.optimizer';
import {flattenTree} from 'rendition-core/layout/FlattenTree/FlattenTree.optimizer';
import {useViewContext} from 'Contexts/ViewContext';
// Note: Might want to refactor some of these so they can be called outside of the react context.

const performRegrouping = (view: AstView, node: EnrichedAstNode, action: () => void) => {
    const canUngroup = checkIfCanMoveChildrenUp(view, node);

    if (node && canUngroup) {
        action();
    } else {
        Sentry.captureMessage('Attempting invalid regrouping');
    }
};

const absolutePositionNode = (view: AstView, node: EnrichedAstNode) => {
    node.inputLayoutAbstraction.absolutePosition = true;
};

export const useRegrouping = (onSelectNodes: (ids: string[]) => void) => {
    const {updateView} = useViewContext();

    const absolutePositionNodes = useCallback(
        (nodes: EnrichedAstNode[]) => {
            updateView(
                (currentView) => {
                    nodes.forEach((node) => absolutePositionNode(currentView, node));

                    return currentView;
                },
                {repairMode: 'lock'}
            );
        },
        [updateView]
    );

    const absolutePositionNodeWithoutChildren = useCallback(
        (nodes: EnrichedAstNode[]) => {
            updateView(
                (currentView) => {
                    nodes.forEach((node) => {
                        performRegrouping(currentView, node, () => {
                            moveChildrenUp(node, currentView);
                            absolutePositionNode(currentView, node);
                        });
                    });

                    return currentView;
                },
                {repairMode: 'lock'}
            );
        },
        [updateView]
    );

    const groupNodes = useCallback(
        async (inputNodes: EnrichedAstNode[]) => {
            if (inputNodes.length < 2) {
                return;
            }
            updateView(
                (currentView) => {
                    const nodes = inputNodes.map((inputNode) => findNodeById(currentView.tree, inputNode.internalId)!);

                    // add all children to selection.
                    const selectedNodeIds = _.uniq([
                        ...nodes.flatMap((node) => getAllChildren(node)),
                        ...nodes.map((node) => node.internalId),
                    ]);

                    const testRun = findRootsOfSelection(currentView, selectedNodeIds);

                    if (testRun.length === 1) {
                        // already grouped
                        onSelectNodes?.([testRun[0].internalId]);
                        return currentView;
                    }

                    // flatten
                    flattenTree(currentView.tree, currentView);

                    const childrenToGroup = findRootsOfSelection(currentView, selectedNodeIds);

                    // need to not flatten user made groups.

                    const parentId = findLowestSharedParent(
                        currentView,
                        childrenToGroup.map((node) => node.internalId)
                    );
                    const newParent = findNodeById(currentView.tree, parentId);

                    if (!newParent) {
                        throw new Error('could not find parent');
                    }

                    // can't remove from view as we loose group info.
                    childrenToGroup.forEach((node) => removeNodeAndChildrenFromView(currentView, node.internalId));

                    // lock children
                    const newGroup = groupNodeBuilder(childrenToGroup, currentView, 'EditorRegrouping');
                    newGroup.userInput.lockChildren = true;
                    newGroup.meta.systemGenerated = false;
                    newGroup.meta.userCreateGroup = true;

                    // find lowest shared parent.

                    // visit once and build data map of all nodes with all children.

                    // Find lowest node with all children.

                    newParent.children.push(newGroup);

                    // rebuild.
                    findBetterParent(currentView.tree, currentView);
                    onSelectNodes([newGroup.internalId]);
                    return currentView;
                },
                {repairMode: 'lock'}
            );
        },
        [onSelectNodes, updateView]
    );

    const unGroupNode = useCallback(
        (nodes: EnrichedAstNode[]) => {
            nodes.forEach((node) => {
                if (node.meta.systemGenerated) {
                    updateView(
                        (currentView) => {
                            performRegrouping(currentView, node, () => {
                                moveChildrenUp(node, currentView);
                            });
                            removeNodeFromView(currentView, node.internalId);
                            return currentView;
                        },
                        {repairMode: 'change'}
                    );
                } else {
                    updateView(
                        (currentView) => {
                            performRegrouping(currentView, node, () => {
                                moveChildrenUp(node, currentView);
                                absolutePositionNode(currentView, node);
                            });
                            return currentView;
                        },
                        {repairMode: 'lock'}
                    );
                }
            });
        },
        [updateView]
    );

    const removeNode = useCallback(
        (nodes: EnrichedAstNode[]) =>
            nodes.forEach((node) => {
                updateView(
                    (currentView) => {
                        performRegrouping(currentView, node, () => {
                            removeNodeFromView(currentView, node.internalId);
                        });
                        return currentView;
                    },
                    {repairMode: 'lock'}
                );
            }),

        [updateView]
    );

    const removeNodeAndChildren = useCallback(
        (nodes: EnrichedAstNode[]) => {
            updateView(
                (currentView) => {
                    nodes.forEach((node) => {
                        performRegrouping(currentView, node, () => {
                            const {parent} = retrieveGroupingContext(currentView, node);
                            parent.children = parent.children.filter((n) => n.internalId !== node.internalId);
                        });
                    });
                    return currentView;
                },
                {repairMode: 'lock'}
            );
        },
        [updateView]
    );

    return {
        moveChildrenUp,
        absolutePositionNodes,
        groupNodes,
        unGroupNode,
        removeNode,
        removeNodeAndChildren,
        absolutePositionNodeWithoutChildren,
    };
};
